<template>
  <div class="st-surface">
    <StToolbar tone="canvas" class="st-surface__bar">
      <span class="st-surface__title">Battleplans</span>
      <span class="st-toolbar-spacer" />
      <FormattingHelp />
    </StToolbar>

    <div class="st-surface__body">
      <PlanList />
      <main class="st-surface__main st-surface__main--gap">
        <!-- No plan selected -->
        <div v-if="!ws.currentPlan.value" class="st-surface__empty">
          Select or add a battleplan to begin editing.
        </div>

        <!-- Header (title + tabs) and the active pane share one rounded card so
             they read as a single unit. -->
        <div v-else class="st-editor-card">
          <TitleBar
            :name="ws.currentPlan.value"
            :subtitle="planSubtitle"
            @rename="onTitleRename"
            @duplicate="onDuplicate"
            @delete="confirmDelete = true"
          >
            <template #tabs>
              <StSegmentedControl v-model="activeTab" :options="tabOptions" size="sm" on-shell />
            </template>
          </TitleBar>

          <!-- Editor stays mounted (v-show) so in-progress map selection survives
               a hop to Rules/Preview and back. -->
          <MapEditor v-show="activeTab === 'editor'" class="st-editor-card__pane" />
          <BattleplanForm
            v-if="activeTab === 'rules'"
            class="st-editor-card__pane st-editor-card__pane--pad"
            :current-plan="ws.currentPlan.value"
          />
          <PreviewPane
            v-else-if="activeTab === 'preview' && ws.battlePack.value"
            class="st-editor-card__pane st-editor-card__pane--pad"
            :battleplan="liveBattleplan"
            :battle-pack="ws.battlePack.value"
          />
        </div>
      </main>
    </div>

    <DeleteConfirmModal
      v-if="confirmDelete"
      :name="ws.currentPlan.value ?? ''"
      @confirm="doDelete"
      @cancel="confirmDelete = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import PlanList from '../components/PlanList.vue';
import MapEditor from '../components/MapEditor.vue';
import BattleplanForm from '../components/BattleplanForm.vue';
import PreviewPane from '../components/PreviewPane.vue';
import FormattingHelp from '../components/FormattingHelp.vue';
import TitleBar from '../components/editor/TitleBar.vue';
import DeleteConfirmModal from '../components/editor/DeleteConfirmModal.vue';
import { StToolbar, StSegmentedControl, type SegmentOption } from '../components/ui/index.js';
import { useWorkspace } from '../state/workspace.js';
import { getPlanTab, setPlanTab, planTabKey, type EditorTab } from '../composables/usePlanTabs.js';
import type { IBattleplan } from '@scrolldex/shared';

const ws = useWorkspace();

const tabOptions: SegmentOption[] = [
  { value: 'editor', label: 'Editor', icon: 'pen-ruler' },
  { value: 'rules', label: 'Rules', icon: 'align-left' },
  { value: 'preview', label: 'Preview', icon: 'eye' },
];

// Active tab is remembered per plan for the session (see usePlanTabs).
const planKey = computed(() => {
  const name = ws.currentPlan.value;
  return name ? planTabKey(ws.battlePack.value, name) : null;
});
const activeTab = computed<EditorTab>({
  get: () => (planKey.value ? getPlanTab(planKey.value) : 'editor'),
  set: (tab) => {
    if (planKey.value) setPlanTab(planKey.value, tab);
  },
});

// ─── Title bar: subtitle, rename, delete ────────────────────────────────────
// Subtitle reflects the 2d6 "roll your battleplan" convention: 6 plans per table,
// numbered Battleplan 1-6, grouped into Table 1, 2, … by every 6th plan.
const planSubtitle = computed(() => {
  const name = ws.currentPlan.value;
  if (!name) return '';
  const idx = ws.planNames.value.indexOf(name);
  if (idx < 0) return '';
  const n = idx + 1;
  const battleplan = ((n - 1) % 6) + 1;
  const table = Math.max(1, Math.ceil(n / 6));
  return `Battleplan ${battleplan} (Table ${table})`;
});

function onTitleRename(next: string) {
  const oldName = ws.currentPlan.value;
  if (!oldName) return;
  if (!ws.renamePlan(oldName, next)) {
    window.alert(`Could not rename to "${next}" — that name is already taken.`);
  }
}

function onDuplicate() {
  const name = ws.currentPlan.value;
  if (name) ws.duplicatePlan(name);
}

const confirmDelete = ref(false);

function doDelete() {
  const name = ws.currentPlan.value;
  if (name) ws.deletePlan(name);
  confirmDelete.value = false;
}

/** Live IBattleplan for the preview: authored data if present, else a stub. */
const liveBattleplan = computed<IBattleplan>(() => {
  const name = ws.currentPlan.value;
  if (!name) return { name: '', rulesText: '', components: [], abilities: [], scoringRules: [] };
  const a = ws.authored.value[name];
  if (a) return a;
  return { name, rulesText: '', components: [], abilities: [], scoringRules: [] };
});
</script>
